import { printHTML, downloadWord } from '../utils/printHelper';
import { useEffect, useState, useMemo } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Trash, Pencil, FirstAidKit, Printer, FileDoc, CaretDown, Eye } from '@phosphor-icons/react';
import { DateInput } from '../components/DateInput';
import { confirmDialog } from '@/components/ConfirmDialog';
import ClientAutocomplete from '../components/ClientAutocomplete';

const fmtDate = (d) => {
  if (!d) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(d)) {
    const [y, m, dd] = d.substring(0, 10).split('-');
    return `${dd}.${m}.${y}`;
  }
  try {
    const dt = new Date(d);
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}.${dt.getFullYear()}`;
  } catch { return d; }
};

const fmtNum = (n) => {
  const v = parseFloat(n) || 0;
  return v.toLocaleString('sr-Latn', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// NETWORKDAYS - count working days (Mon-Fri) between two dates
const networkDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return 0;
  let count = 0;
  const current = new Date(start);
  while (current <= end) {
    const day = current.getDay();
    if (day !== 0 && day !== 6) count++;
    current.setDate(current.getDate() + 1);
  }
  return count;
};

export default function BolovanjePage() {
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [fizickaLica, setFizickaLica] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ client_id: '', ime_prezime: '', jmb: '', prvi_dan_bolovanja: '', dnevni_osnov: 0, procenat_isplate: 1, napomena: '' });
  const [flSearch, setFlSearch] = useState('');
  const [flResults, setFlResults] = useState([]);
  const [showFlDropdown, setShowFlDropdown] = useState(false);
  const [clientRadnici, setClientRadnici] = useState([]);

  // Detail view
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  // Mjesec form  
  const [showMjesecForm, setShowMjesecForm] = useState(false);
  const [activeZahtjevId, setActiveZahtjevId] = useState(null);
  const [mjesecForm, setMjesecForm] = useState({
    period_od: '', period_do: '', dan_isplate: '', broj_radnih_dana: 0
  });

  const [filterClient, setFilterClient] = useState('all');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [e, c] = await Promise.all([api.get('/bolovanje'), api.get('/clients')]);
      setEntries(e.data);
      setClients(c.data);
    } catch { toast.error('Greska pri ucitavanju'); }
    finally { setLoading(false); }
  };

  const searchFizickaLica = async (query) => {
    if (query.length < 1) { setFlResults([]); setShowFlDropdown(false); return; }
    // Search from active workers of selected client
    if (form.client_id && clientRadnici.length > 0) {
      const q = query.toLowerCase();
      const matches = clientRadnici.filter(r => {
        const name = `${r.prezime || ''} ${r.ime || ''}`.toLowerCase();
        return name.includes(q) || (r.jmb || '').includes(q);
      }).map(r => ({ ...r, prezime: r.prezime || '', ime: r.ime || '', jmb: r.jmb || '' }));
      setFlResults(matches.slice(0, 15));
      setShowFlDropdown(matches.length > 0);
      return;
    }
    // If no client or no radnici, search fizicka lica
    if (query.length < 2) return;
    try {
      const r = await api.get(`/fizicka-lica?search=${encodeURIComponent(query)}`);
      setFlResults(r.data.slice(0, 10));
      setShowFlDropdown(r.data.length > 0);
    } catch { setFlResults([]); }
  };

  const fetchClientRadnici = async (clientId) => {
    if (!clientId) { setClientRadnici([]); return; }
    try {
      const r = await api.get(`/prijava-radnika?client_id=${clientId}&status=aktivan`);
      setClientRadnici(r.data);
    } catch { setClientRadnici([]); }
  };

  const sortedClients = useMemo(() =>
    [...clients].sort((a, b) => (a.skraceni_naziv || a.naziv_firme || '').localeCompare(b.skraceni_naziv || b.naziv_firme || '')),
    [clients]
  );

  const getClient = (id) => clients.find(c => c.id === id);
  const getClientName = (id) => { const c = getClient(id); return c ? (c.skraceni_naziv || c.naziv_firme) : ''; };

  // Load radnici when form opens with a client
  useEffect(() => {
    if (showForm && form.client_id) fetchClientRadnici(form.client_id);
  }, [showForm, form.client_id]);

  const filtered = entries.filter(e => filterClient === 'all' || e.client_id === filterClient);

  const handleSubmit = async () => {
    if (!form.client_id || !form.ime_prezime.trim()) { toast.error('Unesite klijenta i ime radnika'); return; }
    try {
      if (editingId) {
        await api.put(`/bolovanje/${editingId}`, form);
        toast.success('Azurirano');
      } else {
        await api.post('/bolovanje', form);
        toast.success('Kreirano');
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ client_id: '', ime_prezime: '', jmb: '', prvi_dan_bolovanja: '', dnevni_osnov: 0, procenat_isplate: 1, napomena: '' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Greska'); }
  };

  const handleEdit = (e) => {
    setForm({ client_id: e.client_id, ime_prezime: e.ime_prezime, jmb: e.jmb || '', prvi_dan_bolovanja: e.prvi_dan_bolovanja || '', dnevni_osnov: e.dnevni_osnov || 0, procenat_isplate: e.procenat_isplate || 1, napomena: e.napomena || '' });
    setFlSearch(e.ime_prezime || '');
    setEditingId(e.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog('Obrisati bolovanje?'))) return;
    try { await api.delete(`/bolovanje/${id}`); toast.success('Obrisano'); fetchAll(); }
    catch { toast.error('Greska'); }
  };

  const openDetail = async (entry) => {
    try {
      const r = await api.get(`/bolovanje/${entry.id}`);
      setSelectedEntry(r.data);
      setShowDetail(true);
    } catch { toast.error('Greska'); }
  };

  const handleMjesecSubmit = async () => {
    if (!selectedEntry || !activeZahtjevId) return;
    const dnevni = selectedEntry.dnevni_osnov || 0;
    const procenat = selectedEntry.procenat_isplate || 1;
    const data = {
      ...mjesecForm,
      dnevni_osnov: dnevni,
      procenat_isplate: procenat,
      iznos: mjesecForm.broj_radnih_dana * dnevni * procenat
    };
    try {
      const r = await api.post(`/bolovanje/${selectedEntry.id}/zahtjev/${activeZahtjevId}/mjesec`, data);
      toast.success('Period dodat');
      setSelectedEntry(r.data);
      setShowMjesecForm(false);
      setMjesecForm({ period_od: '', period_do: '', dan_isplate: '', broj_radnih_dana: 0 });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Greska'); }
  };

  const handleDeleteMjesec = async (zahtjevId, mjesecId) => {
    if (!selectedEntry) return;
    try {
      await api.delete(`/bolovanje/${selectedEntry.id}/zahtjev/${zahtjevId}/mjesec/${mjesecId}`);
      const r = await api.get(`/bolovanje/${selectedEntry.id}`);
      setSelectedEntry(r.data);
      toast.success('Period obrisan');
      fetchAll();
    } catch { toast.error('Greska'); }
  };

  const handleCreateZahtjev = async () => {
    if (!selectedEntry) return;
    try {
      const r = await api.post(`/bolovanje/${selectedEntry.id}/zahtjev`);
      setSelectedEntry(r.data);
      toast.success('Novi zahtjev kreiran');
      fetchAll();
    } catch { toast.error('Greska'); }
  };

  const handleDeleteZahtjev = async (zahtjevId) => {
    if (!selectedEntry || !(await confirmDialog('Obrisati zahtjev sa svim periodima?'))) return;
    try {
      await api.delete(`/bolovanje/${selectedEntry.id}/zahtjev/${zahtjevId}`);
      const r = await api.get(`/bolovanje/${selectedEntry.id}`);
      setSelectedEntry(r.data);
      toast.success('Zahtjev obrisan');
      fetchAll();
    } catch { toast.error('Greska'); }
  };

  const editMjesec = () => {};

  // Calculate auto iznos when fields change  
  const buildHtml = (entry, zahtjev) => {
    const client = getClient(entry.client_id);
    if (!client) return '';
    const mjeseci = zahtjev?.mjeseci || [];
    const dnevni = entry.dnevni_osnov || 0;
    const proc = entry.procenat_isplate || 1;
    const ukupno = mjeseci.reduce((s, m) => s + (m.broj_radnih_dana * dnevni * proc), 0);

    const fD = (d) => fmtDate(d);

    const jmbBoxes = (jmb) => {
      // Render JMB as 13 individual boxes (1 digit each) like on official form
      const digits = String(jmb || '').padEnd(13, ' ').slice(0, 13);
      const cells = Array.from(digits).map(d => `<td style="border:1px solid #000;width:7.7%;text-align:center;padding:2px 0;font-family:Arial,sans-serif;font-size:10pt;font-weight:bold;">${d.trim() || '&nbsp;'}</td>`).join('');
      return `<table style="width:100%;border-collapse:collapse;margin:0;"><tr>${cells}</tr></table>`;
    };

    const rows = mjeseci.map((m, i) => `
      <tr>
        <td style="border:1px solid #000;padding:4px 5px;text-align:center;vertical-align:top;">${i + 1}</td>
        <td style="border:1px solid #000;padding:0;vertical-align:top;">
          <div style="padding:6px 8px 4px;text-align:center;font-weight:bold;">${entry.ime_prezime}</div>
          ${jmbBoxes(entry.jmb)}
        </td>
        <td style="border:1px solid #000;padding:4px 6px;text-align:center;vertical-align:top;">${fD(entry.prvi_dan_bolovanja)}</td>
        <td style="border:1px solid #000;padding:4px 6px;text-align:center;vertical-align:top;">${fD(m.period_od)}</td>
        <td style="border:1px solid #000;padding:4px 6px;text-align:center;vertical-align:top;">${fD(m.period_do)}</td>
        <td style="border:1px solid #000;padding:4px 6px;text-align:center;vertical-align:top;">${fD(m.dan_isplate || m.period_do)}</td>
        <td style="border:1px solid #000;padding:4px 6px;text-align:center;vertical-align:top;">${m.broj_radnih_dana}</td>
        <td style="border:1px solid #000;padding:4px 6px;text-align:right;vertical-align:top;">${(dnevni || 0).toFixed(2)}</td>
        <td style="border:1px solid #000;padding:4px 6px;text-align:center;vertical-align:top;">${proc === 1 ? '100%' : ((proc * 100).toFixed(0) + '%')}</td>
        <td style="border:1px solid #000;padding:4px 6px;text-align:right;vertical-align:top;font-weight:bold;">${(m.broj_radnih_dana * dnevni * proc).toFixed(2)}</td>
      </tr>
    `).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>PSZR-O07 - ${entry.ime_prezime}</title>
<style>
@page { size: A4 landscape; margin: 12mm 10mm; }
* { margin:0; padding:0; box-sizing:border-box; font-family: Arial, sans-serif; }
body { font-family: Arial, sans-serif; font-size: 10pt; color: #000; padding: 10px 15px; position: relative; }
.form-id { position: absolute; top: 10px; right: 15px; font-size: 10pt; font-weight: bold; letter-spacing: 0.5px; }
.fund-header { position: absolute; top: 10px; left: 15px; font-size: 9.5pt; line-height: 1.25; }
.title-wrap { text-align: center; margin: 30px 0 4px; padding-top: 10px; }
.main-title { font-size: 13pt; font-weight: bold; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 2px; }
.sub-title { font-size: 11pt; font-weight: bold; text-transform: uppercase; letter-spacing: 0.3px; }
.section-label { font-size: 10pt; font-weight: bold; margin: 16px 0 6px; }
.company-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 40px; margin: 6px 0 10px; font-size: 10pt; }
.company-grid .fld { padding: 2px 0; }
.company-grid .lbl { font-weight: bold; }
table { width: 100%; border-collapse: collapse; font-size: 8.5pt; margin-top: 6px; }
th { border: 1px solid #000; padding: 5px 4px; text-align: center; background: #fff; font-size: 8pt; font-weight: bold; line-height: 1.2; }
.total-row { font-weight: bold; }
.sig-footer { margin-top: 50px; display: flex; justify-content: space-between; align-items: flex-end; font-size: 10pt; }
.sig-block { text-align: center; flex: 1; }
.sig-line { border-top: 1px solid #000; min-width: 200px; margin: 0 auto; padding-top: 4px; }
.mp { text-align: center; font-weight: bold; font-size: 10pt; padding: 0 30px 6px; }
.napomena { margin-top: 22px; padding: 6px 8px; border-top: 1px solid #000; font-size: 8.5pt; line-height: 1.35; text-align: justify; }
.napomena b { font-weight: bold; }
@media print { html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } body { padding: 0; } }
</style></head><body>
<div class="fund-header">
  Fond za zdravstveno<br>
  osiguranje Crne Gore
</div>
<div class="form-id">PSZR - O07</div>

<div class="title-wrap">
  <div class="main-title">Zahtjev za refundaciju isplaćene naknade zarade</div>
  <div class="sub-title">za vrijeme privremene spriječenosti za rad</div>
</div>

<p class="section-label">Podaci o obvezniku uplate doprinosa:</p>
<div class="company-grid">
  <div class="fld"><span class="lbl">Naziv:</span> ${client.naziv_firme || client.skraceni_naziv || ''}</div>
  <div class="fld"><span class="lbl">Žiro-račun:</span> ${client.ziro_racun_1 || ''}</div>
  <div class="fld"><span class="lbl">PIB:</span> ${client.pib || ''}</div>
  <div class="fld"><span class="lbl">Adresa:</span> ${[client.adresa, client.grad].filter(Boolean).join(', ')}</div>
  <div class="fld"><span class="lbl">Broj OUD-a:</span> ${client.registarski_broj || ''}</div>
  <div class="fld"><span class="lbl">Telefon:</span> ${client.telefon || ''}</div>
  <div class="fld"></div>
  <div class="fld"><span class="lbl">Mail:</span> ${client.email || ''}</div>
</div>

<p class="section-label">Spisak osiguranika kojima su isplaćene naknade zarada:</p>
<table>
  <thead>
    <tr>
      <th rowspan="2" style="width:28px;">R.br.</th>
      <th rowspan="2" style="width:220px;">Prezime i ime<br>Jedinstveni matični broj (JMB)</th>
      <th rowspan="2" style="width:90px;">Prvi dan<br>privremene spriječenosti<br>za rad</th>
      <th colspan="2" style="width:160px;">Period isplate naknade zarade</th>
      <th rowspan="2" style="width:85px;">Dan isplate<br>naknade zarade</th>
      <th rowspan="2" style="width:55px;">Broj<br>radnih<br>dana</th>
      <th rowspan="2" style="width:95px;">Osnov za obračun<br>naknade zarade<br>- dnevni osnov</th>
      <th rowspan="2" style="width:55px;">%<br>isplate</th>
      <th rowspan="2" style="width:95px;">Iznos naknade<br>zarade</th>
    </tr>
    <tr>
      <th style="width:80px;">od</th>
      <th style="width:80px;">do</th>
    </tr>
  </thead>
  <tbody>
    ${rows}
    <tr class="total-row">
      <td colspan="9" style="border:1px solid #000;padding:5px 8px;text-align:right;">UKUPNO:</td>
      <td style="border:1px solid #000;padding:5px 8px;text-align:right;">${ukupno.toFixed(2)}</td>
    </tr>
  </tbody>
</table>

<div class="sig-footer">
  <div class="sig-block">
    <div class="sig-line">(Obračunski radnik)</div>
  </div>
  <div class="mp">(M.P.)</div>
  <div class="sig-block">
    <div class="sig-line">(Potpis ovlašćenog lica)</div>
  </div>
</div>

<div class="napomena">
  <b>Napomena:</b> Poslodavac je dužan da podnese zahtjev za refundaciju naknade zarade područnoj jedinici/filijali Fonda, najkasnije u roku od <b>90 dana</b> od dana isplate naknade zarade zaposlenom, za mjesec za koji se zahtijeva refundacija naknade zarade. Ukoliko poslodavac ne podnese zahtjev za refundaciju naknade zarade u navedenom roku, refundacija naknade zarade ne može se ostvariti.
</div>

<script>window.onload=function(){window.print();window.onafterprint=function(){window.close()}}</script>
</body></html>`;

    return html;
  };

  const handlePrint = (entry, zahtjev) => {
    const client = getClient(entry.client_id);
    if (!client) { toast.error('Klijent nije pronadjen'); return; }
    printHTML(buildHtml(entry, zahtjev));
  };

  const handleWord = (entry, zahtjev) => {
    const client = getClient(entry.client_id);
    if (!client) { toast.error('Klijent nije pronadjen'); return; }
    downloadWord(buildHtml(entry, zahtjev), `PSZR-O07_${(entry.ime_prezime||'radnik').replace(/\s+/g,'_')}.doc`);
  };

  if (loading) return <div className="p-8 text-center text-slate-500">Ucitavanje...</div>;

  return (
    <div className="space-y-6" data-testid="bolovanje-page">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-slate-900">Bolovanje</h3>
          <p className="text-sm text-slate-600 mt-1">Zahtjevi za refundaciju naknade zarade (PSZR-O07)</p>
        </div>
        <Button onClick={() => { setForm({ client_id: '', ime_prezime: '', jmb: '', prvi_dan_bolovanja: '', napomena: '' }); setEditingId(null); setShowForm(true); }}
          className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="bolovanje-add-btn">
          <Plus size={16} className="mr-2" /> Novo bolovanje
        </Button>
      </div>

      {/* Filter */}
      <Card className="p-4">
        <div className="flex gap-3 items-end">
          <div className="space-y-1 min-w-[250px]">
            <Label className="text-xs text-slate-500">Filter po klijentu</Label>
            <ClientAutocomplete clients={clients} value={filterClient === 'all' ? '' : filterClient} onChange={(id) => setFilterClient(id || 'all')} testid="bolovanje-filter-client" placeholder="Svi klijenti — pretraga po nazivu/PIB-u..." />
          </div>
          <p className="text-sm text-slate-500 pb-2">Ukupno: {filtered.length} zapisa</p>
        </div>
      </Card>

      {/* Table */}
      {filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <FirstAidKit size={48} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Nema zapisa o bolovanjima</p>
        </Card>
      ) : (
        <Card className="overflow-hidden" data-testid="bolovanje-table">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Klijent</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Radnik</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">JMB</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Prvi dan</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Perioda</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Ukupno</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(e => {
                  const mj = e.zahtjevi || [];
                  const dnevni = e.dnevni_osnov || 0;
                  const proc = e.procenat_isplate || 1;
                  const sviM = mj.flatMap(z => z.mjeseci || []);
                  const ukupno = sviM.reduce((s, m) => s + (m.broj_radnih_dana * dnevni * proc), 0);
                  return (
                    <tr key={e.id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(e)}>
                      <td className="px-4 py-3 font-medium">{getClientName(e.client_id)}</td>
                      <td className="px-4 py-3 font-medium">{e.ime_prezime}</td>
                      <td className="px-4 py-3 text-xs font-mono">{e.jmb}</td>
                      <td className="px-4 py-3 text-xs">{fmtDate(e.prvi_dan_bolovanja)}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">{mj.length} zahtj.</span>
                      </td>
                      <td className="px-4 py-3 font-medium">{fmtNum(ukupno)} EUR</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1" onClick={ev => ev.stopPropagation()}>
                          <button onClick={() => openDetail(e)} className="p-2 hover:bg-blue-100 rounded" title="Detalji" data-testid={`bolovanje-detail-${e.id}`}>
                            <Eye size={16} className="text-blue-600" />
                          </button>
                          <button onClick={() => handlePrint(e)} className="p-2 hover:bg-green-100 rounded" title="Stampaj PSZR-O07" data-testid={`bolovanje-print-${e.id}`}>
                            <Printer size={16} className="text-green-600" />
                          </button>
                          <button onClick={() => handleWord(e)} className="p-2 hover:bg-indigo-100 rounded" title="Word dokument" data-testid={`bolovanje-word-${e.id}`}>
                            <FileDoc size={16} className="text-indigo-600" />
                          </button>
                          <button onClick={() => handleEdit(e)} className="p-2 hover:bg-amber-100 rounded" title="Izmijeni">
                            <Pencil size={16} className="text-amber-600" />
                          </button>
                          <button onClick={() => handleDelete(e.id)} className="p-2 hover:bg-red-100 rounded" title="Obrisi">
                            <Trash size={16} className="text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Create/Edit Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg" data-testid="bolovanje-form-dialog">
          <DialogHeader><DialogTitle>{editingId ? 'Izmijeni bolovanje' : 'Novo bolovanje'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Klijent (firma) *</Label>
              <ClientAutocomplete clients={clients} value={form.client_id} onChange={(id) => { setForm(prev => ({ ...prev, client_id: id })); fetchClientRadnici(id); }} testid="bolovanje-client-select" placeholder="Pretraga po nazivu ili PIB-u..." />
            </div>
            <div className="space-y-1 relative">
              <Label className="text-xs text-slate-500">Prezime i ime radnika * {form.client_id ? '(aktivni radnici klijenta)' : '(prvo izaberite klijenta)'}</Label>
              <Input value={flSearch || form.ime_prezime}
                onChange={e => {
                  const val = e.target.value;
                  setFlSearch(val);
                  setForm(prev => ({ ...prev, ime_prezime: val }));
                  searchFizickaLica(val);
                }}
                placeholder="Kucajte prezime ili ime..." className="bg-white border-slate-300 text-sm" data-testid="bolovanje-ime-input" autoComplete="off" />
              {showFlDropdown && flResults.length > 0 && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {flResults.map(fl => (
                    <button key={fl.id} type="button" onMouseDown={(e) => {
                      e.preventDefault();
                      const name = `${fl.prezime || ''} ${fl.ime || ''}`.trim();
                      setForm(prev => ({
                        ...prev,
                        ime_prezime: name || fl.jmb,
                        jmb: fl.jmb || prev.jmb,
                      }));
                      setFlSearch(name || fl.jmb);
                      setShowFlDropdown(false);
                    }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-slate-100 last:border-0">
                      <span className="font-medium">{fl.prezime || '?'} {fl.ime || '?'}</span>
                      <span className="text-xs text-slate-400 ml-2">JMB: {fl.jmb || '-'}</span>
                      {fl.naziv_radnog_mjesta && <span className="text-xs text-slate-400 ml-1">({fl.naziv_radnog_mjesta})</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">JMB</Label>
                <Input value={form.jmb} onChange={e => setForm({ ...form, jmb: e.target.value })}
                  placeholder="Jedinstveni maticni broj" className="bg-white border-slate-300 text-sm" data-testid="bolovanje-jmb-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Prvi dan privremene sprijecenosti za rad</Label>
                <DateInput value={form.prvi_dan_bolovanja} onChange={e => setForm({ ...form, prvi_dan_bolovanja: e.target.value })}
                  className="border-slate-300 text-sm" data-testid="bolovanje-prviDan-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Osnov za obracun - dnevni osnov (EUR) *</Label>
                <Input value={form.dnevni_osnov} onChange={e => setForm({ ...form, dnevni_osnov: e.target.value.replace(',', '.') })}
                  placeholder="0,00" className="bg-white border-slate-300 text-sm" data-testid="bolovanje-dnevni-osnov" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">% isplate *</Label>
                <select value={form.procenat_isplate} onChange={e => setForm({ ...form, procenat_isplate: parseFloat(e.target.value) || 0.7 })}
                  className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10" data-testid="bolovanje-procenat">
                  <option value={0.7}>70%</option>
                  <option value={1}>100%</option>
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Napomena</Label>
              <textarea value={form.napomena} onChange={e => setForm({ ...form, napomena: e.target.value })}
                className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm min-h-[60px] resize-y" data-testid="bolovanje-napomena-input" />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowForm(false)}>Odustani</Button>
              <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="bolovanje-save-btn">
                {editingId ? 'Sacuvaj' : 'Kreiraj'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="bolovanje-detail-dialog">
          {selectedEntry && (() => {
            const client = getClient(selectedEntry.client_id);
            const dnevni = selectedEntry.dnevni_osnov || 0;
            const procenat = selectedEntry.procenat_isplate || 1;
            const zahtjevi = selectedEntry.zahtjevi || [];
            const sviMjeseci = zahtjevi.flatMap(z => z.mjeseci || []);
            const ukupnoSve = sviMjeseci.reduce((s, m) => s + (m.broj_radnih_dana * dnevni * procenat), 0);
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FirstAidKit size={20} className="text-red-500" />
                    {selectedEntry.ime_prezime} - {client?.skraceni_naziv || client?.naziv_firme || ''}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 rounded border">
                    <div><span className="text-xs text-slate-500">Klijent:</span><br/><span className="text-sm font-medium">{client?.naziv_firme || ''}</span></div>
                    <div><span className="text-xs text-slate-500">PIB:</span><br/><span className="text-sm font-medium">{client?.pib || ''}</span></div>
                    <div><span className="text-xs text-slate-500">Radnik:</span><br/><span className="text-sm font-medium">{selectedEntry.ime_prezime}</span></div>
                    <div><span className="text-xs text-slate-500">JMB:</span><br/><span className="text-sm font-mono">{selectedEntry.jmb}</span></div>
                    <div><span className="text-xs text-slate-500">Prvi dan privremene sprijecenosti:</span><br/><span className="text-sm">{fmtDate(selectedEntry.prvi_dan_bolovanja)}</span></div>
                    <div><span className="text-xs text-slate-500">Dnevni osnov:</span><br/><span className="text-sm font-medium">{fmtNum(dnevni)} EUR</span></div>
                    <div><span className="text-xs text-slate-500">% isplate:</span><br/><span className="text-sm font-medium">{!procenat && procenat !== 0 ? '-' : procenat === 1 ? '100%' : ((procenat * 100).toFixed(0) + '%')}</span></div>
                    <div><span className="text-xs text-slate-500">Ukupno svi zahtjevi:</span><br/><span className="text-sm font-bold text-blue-700">{fmtNum(ukupnoSve)} EUR ({zahtjevi.length} zahtjeva)</span></div>
                  </div>

                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-800">Zahtjevi za refundaciju</h4>
                    <Button size="sm" onClick={handleCreateZahtjev} className="bg-blue-600 hover:bg-blue-700 text-white text-xs" data-testid="bolovanje-add-zahtjev">
                      <Plus size={14} className="mr-1" /> Novi zahtjev
                    </Button>
                  </div>

                  {zahtjevi.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">Nema zahtjeva - kliknite "Novi zahtjev" da kreirate prvi</p>
                  ) : (
                    <div className="space-y-4">
                      {zahtjevi.map((z, zi) => {
                        const zMjeseci = z.mjeseci || [];
                        const zUkupno = zMjeseci.reduce((s, m) => s + (m.broj_radnih_dana * dnevni * procenat), 0);
                        return (
                          <div key={z.id} className="border border-slate-200 rounded-lg overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-2 bg-slate-100 border-b">
                              <span className="text-sm font-semibold text-slate-700">Zahtjev #{zi + 1} <span className="text-xs font-normal text-slate-500">({fmtDate(z.created_at?.split('T')[0])})</span></span>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => handlePrint(selectedEntry, z)} className="text-green-600 border-green-200 hover:bg-green-50 text-xs h-7">
                                  <Printer size={13} className="mr-1" /> Stampaj PSZR-O07
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleWord(selectedEntry, z)} className="text-indigo-600 border-indigo-200 hover:bg-indigo-50 text-xs h-7">
                                  <FileDoc size={13} className="mr-1" /> Word
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => { setActiveZahtjevId(z.id); setMjesecForm({ period_od: '', period_do: '', dan_isplate: '', broj_radnih_dana: 0 }); setShowMjesecForm(true); }}
                                  className="text-blue-600 border-blue-200 hover:bg-blue-50 text-xs h-7">
                                  <Plus size={13} className="mr-1" /> Dodaj period
                                </Button>
                                <button onClick={() => handleDeleteZahtjev(z.id)} className="p-1 hover:bg-red-100 rounded" title="Obrisi zahtjev"><Trash size={14} className="text-red-500" /></button>
                              </div>
                            </div>
                            {zMjeseci.length === 0 ? (
                              <p className="text-xs text-slate-400 text-center py-3">Dodajte periode za ovaj zahtjev</p>
                            ) : (
                              <table className="w-full text-left text-sm text-slate-600">
                                <thead className="bg-slate-50">
                                  <tr>
                                    <th className="px-3 py-1.5 text-xs font-semibold text-slate-500 border-b">#</th>
                                    <th className="px-3 py-1.5 text-xs font-semibold text-slate-500 border-b">Period od</th>
                                    <th className="px-3 py-1.5 text-xs font-semibold text-slate-500 border-b">Period do</th>
                                    <th className="px-3 py-1.5 text-xs font-semibold text-slate-500 border-b">Dan isplate</th>
                                    <th className="px-3 py-1.5 text-xs font-semibold text-slate-500 border-b">Radni dani</th>
                                    <th className="px-3 py-1.5 text-xs font-semibold text-slate-500 border-b">Dnevni osnov</th>
                                    <th className="px-3 py-1.5 text-xs font-semibold text-slate-500 border-b">%</th>
                                    <th className="px-3 py-1.5 text-xs font-semibold text-slate-500 border-b">Iznos</th>
                                    <th className="px-3 py-1.5 text-xs font-semibold text-slate-500 border-b"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {zMjeseci.map((m, i) => {
                                    const iz = m.broj_radnih_dana * dnevni * procenat;
                                    return (
                                      <tr key={m.id} className="border-b hover:bg-slate-50">
                                        <td className="px-3 py-1.5 text-center">{i + 1}</td>
                                        <td className="px-3 py-1.5">{fmtDate(m.period_od)}</td>
                                        <td className="px-3 py-1.5">{fmtDate(m.period_do)}</td>
                                        <td className="px-3 py-1.5">{fmtDate(m.dan_isplate)}</td>
                                        <td className="px-3 py-1.5 text-center">{m.broj_radnih_dana}</td>
                                        <td className="px-3 py-1.5 text-right">{fmtNum(dnevni)}</td>
                                        <td className="px-3 py-1.5 text-center">{procenat === 1 ? '100%' : ((procenat * 100).toFixed(0) + '%')}</td>
                                        <td className="px-3 py-1.5 text-right font-bold">{fmtNum(iz)}</td>
                                        <td className="px-3 py-1.5">
                                          <button onClick={() => handleDeleteMjesec(z.id, m.id)} className="p-1 hover:bg-red-100 rounded"><Trash size={13} className="text-red-500" /></button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                  <tr className="bg-slate-100 font-bold text-sm">
                                    <td colSpan={7} className="px-3 py-1.5 text-right">UKUPNO:</td>
                                    <td className="px-3 py-1.5 text-right">{fmtNum(zUkupno)} EUR</td>
                                    <td></td>
                                  </tr>
                                </tbody>
                              </table>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Add/Edit Mjesec Dialog */}
      <Dialog open={showMjesecForm} onOpenChange={setShowMjesecForm}>
        <DialogContent className="max-w-md" data-testid="bolovanje-mjesec-dialog">
          <DialogHeader><DialogTitle>Dodaj period bolovanja</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Period od *</Label>
                <DateInput value={mjesecForm.period_od} onChange={e => {
                  const od = e.target.value;
                  const days = networkDays(od, mjesecForm.period_do);
                  setMjesecForm({ ...mjesecForm, period_od: od, broj_radnih_dana: days });
                }}
                  className="border-slate-300 text-sm" data-testid="mjesec-od" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Period do *</Label>
                <DateInput value={mjesecForm.period_do} onChange={e => {
                  const do_ = e.target.value;
                  const days = networkDays(mjesecForm.period_od, do_);
                  setMjesecForm({ ...mjesecForm, period_do: do_, broj_radnih_dana: days });
                }}
                  className="border-slate-300 text-sm" data-testid="mjesec-do" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Dan isplate</Label>
                <DateInput value={mjesecForm.dan_isplate} onChange={e => setMjesecForm({ ...mjesecForm, dan_isplate: e.target.value })}
                  className="border-slate-300 text-sm" data-testid="mjesec-isplata" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Broj radnih dana * (auto)</Label>
                <Input type="number" value={mjesecForm.broj_radnih_dana} onChange={e => setMjesecForm({ ...mjesecForm, broj_radnih_dana: parseInt(e.target.value) || 0 })}
                  className="bg-white border-slate-300 text-sm" data-testid="mjesec-radni-dani" />
              </div>
            </div>
            {selectedEntry && (
              <div className="p-3 bg-blue-50 rounded border border-blue-200">
                <p className="text-sm font-medium text-blue-800">
                  Iznos: {fmtNum(mjesecForm.broj_radnih_dana * (selectedEntry.dnevni_osnov || 0) * (selectedEntry.procenat_isplate || 1))} EUR
                </p>
                <p className="text-xs text-blue-600">{mjesecForm.broj_radnih_dana} dana x {fmtNum(selectedEntry.dnevni_osnov || 0)} x {(selectedEntry.procenat_isplate || 1) === 1 ? '100%' : (((selectedEntry.procenat_isplate || 1) * 100).toFixed(0) + '%')}</p>
              </div>
            )}
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowMjesecForm(false)}>Odustani</Button>
              <Button onClick={handleMjesecSubmit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="mjesec-save-btn">
                Dodaj
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
